<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div v-if="targetingCriteria != null && targetingCriteria.filter((x) => !x.isUpdatePushedExternally).length > 0">
    <div
      v-if="wrappedInPane"
      class="form-section group">
      <span class="icon"><icon-colored-weather /></span>
      {{ capitalize($t('messages.weatherTargeting')) }}
      <div class="actions">
        <slot name="actions" />
      </div>
    </div>
    <feature-message
      v-if="isSidekickOrganization"
      :message="capitalize($t('messages.sidekickTestFeatureDefault'))" />
    <transition name="squish fade">
      <div
        v-show="isVisible"
        :class="{ 'form-section-content': wrappedInPane }">
        <!-- /////////////////////////////////////////////////////////// -->
        <!-- WEATHER LOCATION SELECTION -->
        <!-- /////////////////////////////////////////////////////////// -->

        <div
          v-if="enableDestinationWeather && supportsDestinationWeather"
          class="form-group row">
          <form-label>{{ capitalize($t('names.location', { n: 1 })) }}</form-label>
          <div class="col-sm-9">
            <selectable-tiles
              :items="weatherLocations"
              :selected-items="selectedWeatherLocationsInternal"
              :should-display-labels="true"
              label-property-name="label"
              description-property-name="description"
              :should-display-icons="true"
              icon-property-name="icon"
              tile-padding="1rem"
              :show-primary-badge-color="false"
              :should-display-checkmark-on-selected-items="true"
              :is-multiselect="true"
              :show-only-button="true"
              :allow-empty="false"
              @selection-changed="
                (x) => {
                  selectedWeatherLocationsInternal = x;
                }
              " />
          </div>
        </div>

        <!-- /////////////////////////////////////////////////////////// -->
        <!-- LOCAL WEATHER (where the screen playing the ad is located)" -->
        <!-- /////////////////////////////////////////////////////////// -->

        <div
          v-if="isLocalTargetingEnabled"
          :class="{ 'well light': enableDestinationWeather && supportsDestinationWeather }">
          <div
            v-if="enableDestinationWeather && supportsDestinationWeather"
            class="well-badge">
            <span class="badge badge-info">
              <icon-location-2 />
              <span>{{ capitalize($t('messages.local')) }}</span>
            </span>
          </div>
          <weather-targeting-editor-rule
            ref="localRule"
            :temperature-code="temperatureCode"
            :temperature-value="temperatureValue"
            :is-read-only="isReadOnly"
            :selected-weather-conditions="selectedWeatherConditions"
            :entity="entity"
            :targeting-criteria="targetingCriteria"
            temperature-targeting-criteria-type="TEMPERATURE"
            weather-targeting-criteria-type="WEATHER"
            @selected-weather-conditions-changed="(x) => $emit('selectedWeatherConditionsChanged', x)"
            @temperature-code-changed="(x) => $emit('temperatureCodeChanged', x)"
            @temperature-value-changed="(x) => $emit('temperatureValueChanged', x)" />
          <u-v-targeting-editor
            v-if="enableUVTargeting || (selectedUvConditions != null && selectedUvConditions.length > 0)"
            :wrapped-in-pane="false"
            :is-read-only="isReadOnly"
            :selected-u-v-conditions="selectedUvConditions"
            :targeting-criteria="targetingCriteria"
            @selected-u-v-conditions-changed="(x) => $emit('selectedUVConditionsChanged', x)" />
        </div>

        <!-- /////////////////////////////////////////////////////////// -->
        <!-- DESTINATION WEATHER (the location being advertised)" -->
        <!-- /////////////////////////////////////////////////////////// -->

        <div
          v-if="isDestinationTargetingEnabled"
          class="well light">
          <div class="well-badge">
            <span class="badge badge-info">
              <icon-travel-destination />
              <span>{{ capitalize($t('messages.destination')) }}</span>
            </span>
          </div>
          <weather-targeting-editor-rule
            ref="destinationRule"
            :latitude="destinationLatitude"
            :longitude="destinationLongitude"
            :temperature-code="destinationTemperatureCode"
            :temperature-value="destinationTemperatureValue"
            :snow-accumulation-code="destinationSnowAccumulationCode"
            :snow-accumulation-value="destinationSnowAccumulationValue"
            :rain-accumulation-code="destinationRainAccumulationCode"
            :rain-accumulation-value="destinationRainAccumulationValue"
            :is-read-only="isReadOnly"
            :selected-weather-conditions="selectedDestinationWeatherConditions"
            :entity="entity"
            :targeting-criteria="targetingCriteria"
            :requires-location="true"
            temperature-targeting-criteria-type="DESTINATION_TEMPERATURE"
            weather-targeting-criteria-type="DESTINATION_WEATHER"
            snow-targeting-criteria-type="DESTINATION_SNOW"
            rain-targeting-criteria-type="DESTINATION_RAIN"
            @selected-weather-conditions-changed="(x) => $emit('selectedDestinationWeatherConditionsChanged', x)"
            @latitude-changed="(x) => $emit('destinationLatitudeChanged', x)"
            @longitude-changed="(x) => $emit('destinationLongitudeChanged', x)"
            @temperature-code-changed="(x) => $emit('destinationTemperatureCodeChanged', x)"
            @temperature-value-changed="(x) => $emit('destinationTemperatureValueChanged', x)"
            @snow-accumulation-code-changed="(x) => $emit('destinationSnowAccumulationCodeChanged', x)"
            @snow-accumulation-value-changed="(x) => $emit('destinationSnowAccumulationValueChanged', x)"
            @rain-accumulation-code-changed="(x) => $emit('destinationRainAccumulationCodeChanged', x)"
            @rain-accumulation-value-changed="(x) => $emit('destinationRainAccumulationValueChanged', x)" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script type="text/babel">
  import { capitalize, markRaw } from 'vue';
  import { mapGetters } from 'vuex';

  import Colors from '@/Colors';
  import FormLabel from '@/common/components/controls/FormLabel';
  import SelectableTiles from '@/common/components/controls/SelectableTiles';
  import UVTargetingEditor from '@/common/components/controls/targeting/uv/UVTargetingEditor';
  import FeatureMessage from '@/common/components/generic-messages/FeatureMessage.vue';
  import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
  import { IconColoredLocation2, IconColoredTravelDestination, IconColoredWeather } from '@/common/icons/colored';
  import { IconLocation2, IconTravelDestination } from '@/common/icons/monochrome';
  import { useTargetingCriteria } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import i18n from '@/vueI18n';

  import WeatherTargetingEditorRule from './WeatherTargetingEditorRule';

  export default {
    name: 'WeatherTargeting',
    components: {
      FormLabel,
      IconColoredWeather,
      IconLocation2,
      IconTravelDestination,
      SelectableTiles,
      UVTargetingEditor,
      WeatherTargetingEditorRule,
      FeatureMessage,
    },
    props: {
      temperatureCode: {
        type: String,
        default: TargetingCriteriaType.DailyMaxAbove,
      },
      temperatureValue: {
        type: Number,
        default: 20,
      },
      selectedWeatherConditions: {
        type: Array,
        default: () => [],
      },
      destinationLatitude: {
        type: Number,
        default: null,
      },
      destinationLongitude: {
        type: Number,
        default: null,
      },
      destinationTemperatureCode: {
        type: String,
        default: TargetingCriteriaType.DailyMaxAbove,
      },
      destinationTemperatureValue: {
        type: Number,
        default: 20,
      },
      destinationSnowAccumulationCode: {
        type: String,
        default: 'SNOW_CM_LAST_12H_ABOVE',
      },
      destinationSnowAccumulationValue: {
        type: Number,
        default: 1,
      },
      destinationRainAccumulationCode: {
        type: String,
        default: 'RAIN_MM_LAST_12H_ABOVE',
      },
      destinationRainAccumulationValue: {
        type: Number,
        default: 1,
      },
      selectedDestinationWeatherConditions: {
        type: Array,
        default: () => [],
      },
      isReadOnly: {
        type: Boolean,
        default: false,
      },
      wrappedInPane: {
        type: Boolean,
        default: true,
      },
      isVisible: {
        type: Boolean,
        default: true,
      },
      entity: {
        type: Object,
        default: null,
      },
      enableLocalWeather: {
        type: Boolean,
        default: true,
      },
      enableDestinationWeather: {
        type: Boolean,
        default: true,
      },
      selectedUvConditions: {
        type: Array,
        default: () => [],
      },
      enableUVTargeting: {
        type: Boolean,
        default: true,
      },
    },
    emits: [
      'clearedDestinationTargeting',
      'clearedLocalTargeting',
      'selectedDestinationWeatherConditionsChanged',
      'destinationLatitudeChanged',
      'destinationLongitudeChanged',
      'destinationTemperatureCodeChanged',
      'destinationTemperatureValueChanged',
      'destinationSnowAccumulationCodeChanged',
      'destinationSnowAccumulationValueChanged',
      'destinationRainAccumulationCodeChanged',
      'destinationRainAccumulationValueChanged',
      'selectedWeatherConditionsChanged',
      'temperatureCodeChanged',
      'temperatureValueChanged',
      'selectedUVConditionsChanged',
    ],
    data() {
      return {
        areWeatherConditionsEnabled: false,
        colors: Colors,
        i18n,
        selectedWeatherLocationsInternal: [],
        targetingCriteriaQuery: useTargetingCriteria(),
      };
    },
    computed: {
      ...mapGetters('orgApp', ['isSidekickOrganization']),
      targetingCriteria() {
        return this.targetingCriteriaQuery.items;
      },
      isLocalTargetingEnabled() {
        return this.selectedWeatherLocationsInternal.some((x) => x.id === 'local');
      },
      isDestinationTargetingEnabled() {
        return this.selectedWeatherLocationsInternal.some((x) => x.id === 'destination');
      },
      supportsDestinationWeather() {
        return this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.DestinationWeather) != null;
      },
      weatherLocations() {
        return [
          {
            id: 'local',
            label: capitalize(i18n.global.t('messages.local')),
            description: capitalize(i18n.global.t('messages.whereTheScreenIsLocated')),
            icon: markRaw(IconColoredLocation2),
          },
          ...(this.enableDestinationWeather && this.supportsDestinationWeather
            ? [
                {
                  id: 'destination',
                  label: capitalize(i18n.global.t('messages.destination')),
                  description: capitalize(i18n.global.t('messages.theLocationBeingAdvertised')),
                  icon: markRaw(IconColoredTravelDestination),
                },
              ]
            : []),
        ];
      },
    },
    watch: {
      isLocalTargetingEnabled(val) {
        if (!val) {
          this.$emit('clearedLocalTargeting');
        }
      },
      isDestinationTargetingEnabled(val) {
        if (!val) {
          this.$emit('clearedDestinationTargeting');
        }
      },
    },
    async beforeMount() {
      await waitForPendingQueries([this.targetingCriteriaQuery]);
    },
    mounted() {
      const weatherLocations = [];
      if (this.selectedWeatherConditions != null || this.temperatureCode != null || this.temperatureValue != null) {
        const local = this.weatherLocations.find((x) => x.id === 'local');
        if (local != null) {
          weatherLocations.push(local);
        }
      }
      if (
        this.supportsDestinationWeather &&
        (this.selectedDestinationWeatherConditions != null ||
          this.destinationTemperatureCode != null ||
          this.destinationTemperatureValue != null ||
          this.destinationSnowAccumulationCode != null ||
          this.destinationSnowAccumulationValue != null ||
          this.destinationRainAccumulationCode != null ||
          this.destinationRainAccumulationValue != null)
      ) {
        const destination = this.weatherLocations.find((x) => x.id === 'destination');
        if (destination != null) {
          weatherLocations.push(destination);
        }
      }

      if (weatherLocations.length === 0) {
        weatherLocations.push(this.weatherLocations[0]);
      }
      this.selectedWeatherLocationsInternal = weatherLocations;
    },
    methods: {
      capitalize(s) {
        return capitalize(s);
      },
      validate() {
        let validationResultLocal = true;
        if (this.$refs.localRule != null) {
          validationResultLocal = this.$refs.localRule.validate();
        }

        let validationResultDestination = true;
        if (this.$refs.destinationRule != null) {
          validationResultDestination = this.$refs.destinationRule.validate();
        }

        if (validationResultLocal === true && validationResultDestination === true) {
          return true;
        }

        let extras = {};
        if (validationResultLocal !== true) {
          extras = { ...extras, ...validationResultLocal };
        }
        if (validationResultDestination !== true) {
          extras = { ...extras, ...validationResultDestination };
        }
        return extras;
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  .temperature-controls-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    > * {
      margin-right: 1.2rem;
      margin-bottom: 0.8rem;

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  #weather_temp_limit {
    .multiselect {
      width: 26rem;
    }
  }

  #weather_temp_comparator {
    .multiselect {
      width: 13rem;
    }
  }

  #weather_temp_target {
    width: 10rem;
  }

  @media (max-width: 991px) {
    #weather_temp_limit {
      .multiselect {
        width: 100%;
      }
    }
  }
</style>
